// src/contexts/ThemeContext.tsx
import React, {
    createContext,
    useContext,
    useState,
    useEffect, type ReactNode,
} from 'react'
import { envStorage } from '@/utils/envCache'

// 扩展主题类型以包含系统模式
type ThemeMode = 'light' | 'dark' | 'system'
type ResolvedTheme = 'light' | 'dark'

interface ThemeContextType {
    themeMode: ThemeMode
    resolvedTheme: ResolvedTheme
    setThemeMode: (mode: ThemeMode) => void
    toggleTheme: (mode?: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [themeMode, setThemeMode] = useState<ThemeMode>(envStorage.getItem('themeMode') as ThemeMode || 'system')
    const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>('light')
    const [isMounted, setIsMounted] = useState(false)

    // 初始化主题（客户端渲染后）
    useEffect(() => {
        setIsMounted(true)
    }, [])

    useEffect(() => {
        if (!isMounted) return

        if (resolvedTheme === 'dark') {
            document.documentElement.classList.add('dark')
            document.documentElement.setAttribute('data-theme', 'dark')
        } else {
            document.documentElement.classList.remove('dark')
            document.documentElement.setAttribute('data-theme', 'light')
        }

        envStorage.setItem('themeMode', themeMode)
    }, [resolvedTheme, themeMode, isMounted])

    useEffect(() => {
        if (!isMounted) return

        const storedTheme = envStorage.getItem('themeMode') as ThemeMode | null
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

        if (storedTheme) {
            setThemeMode(storedTheme)
        } else if (prefersDark) {
            setThemeMode('dark')
        } else {
            setThemeMode('light')
        }
    }, [isMounted])
    // 监听系统主题变化
    useEffect(() => {
        if (!isMounted) return

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')

        // 设置初始值
        const updateResolvedTheme = () => {
            if (themeMode === 'system') {
                setResolvedTheme(mediaQuery.matches ? 'dark' : 'light')
            } else {
                setResolvedTheme(themeMode)
            }
        }

        updateResolvedTheme()

        // 添加监听器
        const handler = () => {
            if (themeMode === 'system') {
                setResolvedTheme(mediaQuery.matches ? 'dark' : 'light')
            }
        }

        mediaQuery.addEventListener('change', handler)

        return () => mediaQuery.removeEventListener('change', handler)
    }, [themeMode, isMounted])

    // 当主题模式改变时的处理
    useEffect(() => {
        if (!isMounted) return

        const storedTheme = localStorage.getItem('themeMode') as ThemeMode | null
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches

        if (storedTheme) {
            setThemeMode(storedTheme)
        } else if (prefersDark) {
            setThemeMode('dark')
        } else {
            setThemeMode('light')
        }
    }, [isMounted])

    // 当实际应用的主题改变时更新 DOM
    useEffect(() => {
        if (!isMounted) return

        if (resolvedTheme === 'dark') {
            document.documentElement.classList.add('dark')
            document.documentElement.setAttribute('data-theme', 'dark')
        } else {
            document.documentElement.classList.remove('dark')
            document.documentElement.setAttribute('data-theme', 'light')
        }

        localStorage.setItem('themeMode', themeMode)
    }, [resolvedTheme, themeMode, isMounted])

    // 切换主题模式（循环切换：light → dark → system）
    const toggleTheme = (mode?: ThemeMode) => {
        if (mode) {
            setThemeMode(mode)
        } else {
            // 如果没有传入参数，保持原来的循环切换逻辑
            setThemeMode(prev => {
                if (prev === 'light') return 'dark'
                if (prev === 'dark') return 'system'
                return 'light'
            })
        }
    }

    // 直接设置主题模式
    const setTheme = (mode: ThemeMode) => {
        setThemeMode(mode)
    }

    return (
        <ThemeContext.Provider
            value={{
                themeMode,
                resolvedTheme,
                setThemeMode: setTheme,
                toggleTheme
            }}
        >
            {children}
        </ThemeContext.Provider>
    )
}

export const useTheme = () => {
    const context = useContext(ThemeContext)
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider')
    }
    return context
}
